import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import AdminLayout from './components/AdminLayout';
import AccessLockedState from './components/AccessLockedState';
import BusinessPortalSkeleton from './components/BusinessPortalSkeleton';
import BusinessPortalView from './components/BusinessPortalView';
import UserManagementView from './components/UserManagementView';
import {
  getBusinesses,
  updateBusiness,
  updateBusinessOrder,
  updateBusinessPreferences,
} from '../../services/businessService';
import {
  buildBusinessPortalUrl,
  isBusinessOpen,
  isLaunchableBusiness,
} from '../../utils/portalRegistry';

function AdminDashboardPage({ user, token, onLogout, onProfileUpdate, onPasswordChanged }) {
  const [activeView, setActiveView] = useState('E-Portal');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [businessFlagColors, setBusinessFlagColors] = useState({});
  const [openingBusinessId, setOpeningBusinessId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isSavingOrderRef = useRef(false);
  const queuedBusinessIdsRef = useRef(null);
  const activeSaveToastIdRef = useRef(null);

  useEffect(() => {
    let ignore = false;

    async function loadBusinesses() {
      setLoading(true);
      setError('');

      try {
        const response = await getBusinesses(token);
        const rows = response.data || [];
        const firstOpenBusiness = rows.find((item) => isBusinessOpen(item));

        if (!ignore) {
          setBusinesses(rows);
          setBusinessFlagColors(
            rows.reduce((nextColors, business) => {
              if (business.flag_color) {
                nextColors[business.id] = business.flag_color;
              }

              return nextColors;
            }, {})
          );
          setSelectedBusinessId((current) => current || firstOpenBusiness?.id || rows[0]?.id || null);
        }
      } catch (requestError) {
        if (!ignore) {
          setError(requestError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadBusinesses();

    return () => {
      ignore = true;
    };
  }, [token]);

  const visibleBusinesses = businesses;

  const selectedBusiness =
    visibleBusinesses.find((item) => item.id === selectedBusinessId) || visibleBusinesses[0] || null;

  function handleBusinessSelect(business) {
    setSelectedBusinessId(business.id);
  }

  function handleBusinessOpen(business) {
    if (!isBusinessOpen(business)) {
      return;
    }

    if (isLaunchableBusiness(business)) {
      setOpeningBusinessId(business.id);

      try {
        const portalUrl = buildBusinessPortalUrl({
          business,
          portalRole: user?.role_name === 'admin-custom' ? 'admin-custom' : 'admin',
          token,
        });

        window.location.href = portalUrl;
      } catch (requestError) {
        setOpeningBusinessId(null);
        toast.error(requestError.message || 'Unable to open the selected portal.');
      }
    }
  }

  function handleUserManagementOpen(business) {
    setSelectedBusinessId(business.id);
    setActiveView('User Management');
  }

  async function persistBusinessOrder(nextBusinessIds) {
    queuedBusinessIdsRef.current = nextBusinessIds;

    if (isSavingOrderRef.current) {
      return;
    }

    isSavingOrderRef.current = true;

    while (queuedBusinessIdsRef.current) {
      const businessIdsToSave = queuedBusinessIdsRef.current;
      queuedBusinessIdsRef.current = null;

      try {
        await updateBusinessOrder(token, businessIdsToSave);

        if (activeSaveToastIdRef.current) {
          toast.dismiss(activeSaveToastIdRef.current);
        }

        activeSaveToastIdRef.current = toast.success('Business order saved.');
      } catch (requestError) {
        if (activeSaveToastIdRef.current) {
          toast.dismiss(activeSaveToastIdRef.current);
        }

        activeSaveToastIdRef.current = toast.error(
          requestError.message || 'Failed to save the new business order.'
        );
      }
    }

    isSavingOrderRef.current = false;
  }

  function handleBusinessesReorder(nextBusinesses) {
    setBusinesses(nextBusinesses);
    persistBusinessOrder(nextBusinesses.map((business) => business.id));
  }

  async function handleBusinessLogoChange(business, logoUpdate) {
    const storedBusiness = businesses.find((item) => item.id === business.id) || business;
    const payload = new FormData();

    payload.append('name', storedBusiness.name);
    payload.append('system_type', storedBusiness.system_type);
    payload.append('status', storedBusiness.status);
    payload.append('portal_access', storedBusiness.portal_access ?? storedBusiness.portal_state ?? 'open');

    if (logoUpdate?.file) {
      payload.append('logo_file', logoUpdate.file);
    }

    if (logoUpdate?.removeLogo) {
      payload.append('remove_logo', 'true');
    }

    const response = await updateBusiness(token, business.id, payload);
    const updatedBusiness = response.data || {
      ...storedBusiness,
      logo: logoUpdate?.removeLogo ? null : storedBusiness.logo,
    };

    setBusinesses((currentBusinesses) =>
      currentBusinesses.map((item) =>
        item.id === business.id
          ? {
              ...item,
              ...updatedBusiness,
              branch_count: item.branch_count,
              sort_order: item.sort_order,
              flag_color: item.flag_color,
            }
          : item
      )
    );
    toast.success(logoUpdate?.removeLogo ? 'Business logo removed.' : 'Business logo uploaded.');
  }

  async function handleBusinessRename(business, businessName) {
    const normalizedName = businessName.trim();
    const storedBusiness = businesses.find((item) => item.id === business.id) || business;

    if (!normalizedName) {
      throw new Error('Business name is required.');
    }

    const payload = {
      name: normalizedName,
      system_type: business.system_type,
      status: business.status,
      portal_access: business.portal_access ?? business.portal_state ?? 'open',
      logo: storedBusiness.logo || null,
    };

    const response = await updateBusiness(token, business.id, payload);
    const updatedBusiness = response.data || { ...business, name: normalizedName };

    setBusinesses((currentBusinesses) =>
      currentBusinesses.map((item) =>
        item.id === business.id
          ? {
              ...item,
              ...updatedBusiness,
              logo: item.logo,
              branch_count: item.branch_count,
              sort_order: item.sort_order,
              flag_color: item.flag_color,
            }
          : item
      )
    );

    toast.success('Business name updated.');
  }

  async function handleBusinessFlagColorChange(business, colorValue) {
    const normalizedValue = colorValue === 'default' ? undefined : colorValue;
    const response = await updateBusinessPreferences(token, business.id, {
      flag_color: colorValue,
    });
    const savedColor = response.data?.flag_color || normalizedValue;

    setBusinessFlagColors((currentColors) => {
      const nextColors = { ...currentColors };

      if (savedColor) {
        nextColors[business.id] = savedColor;
      } else {
        delete nextColors[business.id];
      }

      return nextColors;
    });
    setBusinesses((currentBusinesses) =>
      currentBusinesses.map((item) =>
        item.id === business.id ? { ...item, flag_color: savedColor || null } : item
      )
    );

    toast.success(
      savedColor ? 'Business portal theme updated.' : 'Business portal theme reset to default.'
    );
  }

  return (
    <AdminLayout
      user={user}
      token={token}
      selectedBusiness={selectedBusiness}
      onLogout={onLogout}
      onProfileUpdate={onProfileUpdate}
      onPasswordChanged={onPasswordChanged}
      activeView={activeView}
      onSelectView={setActiveView}
    >
      {loading && activeView === 'E-Portal' ? <BusinessPortalSkeleton /> : null}

      {error ? (
        <div className="mb-4 rounded-2xl border border-[#f0d4cb] bg-white px-4 py-4 text-[#b4533f]">
          {error}
        </div>
      ) : null}

      {!loading && !error && activeView === 'E-Portal' ? (
        <BusinessPortalView
          businesses={visibleBusinesses}
          openingBusinessId={openingBusinessId}
          selectedBusinessId={selectedBusinessId}
          onOpenBusiness={handleBusinessOpen}
          onOpenUserManagement={handleUserManagementOpen}
          onSelectBusiness={handleBusinessSelect}
          onReorderBusinesses={handleBusinessesReorder}
          businessFlagColors={businessFlagColors}
          onChangeBusinessLogo={handleBusinessLogoChange}
          onChangeBusinessFlagColor={handleBusinessFlagColorChange}
          onRenameBusiness={handleBusinessRename}
        />
      ) : null}

      {!loading && !error && activeView === 'User Management' ? (
        isBusinessOpen(selectedBusiness) ? (
          <UserManagementView
            currentUser={user}
            token={token}
            businesses={visibleBusinesses}
            selectedBusinessId={selectedBusinessId}
          />
        ) : (
          <AccessLockedState businessName={selectedBusiness?.name} />
        )
      ) : null}
    </AdminLayout>
  );
}

export default AdminDashboardPage;
